/**
 * Cookie Consent Wrapper
 *
 * Prepares the options for `CookieConsent` and handles additional events.
 *
 * {@link https://github.com/orestbida/cookieconsent}
 */
import * as CookieConsent from 'vanilla-cookieconsent';

/**
 * Google Analytics / Tag Manager consent mode state.
 *
 * @typedef {('granted'|'denied')} GoogleConsentState
 */

/**
 * Google Analytics / Tag Manager consent mode types and states.
 *
 * {@link https://developers.google.com/tag-platform/security/guides/consent?consentmode=advanced}
 *
 * @typedef {Object<string, ?GoogleConsentState>} GoogleConsentTypeMap
 */

/**
 * Google Analytics / Tag Manager consent states grouped by Cookie Consent categories.
 *
 * @typedef {Object<string, GoogleConsentTypeMap>} LocomotiveGoogleConsentTypeMap
 */

export default class CookieConsentManager {
    /**
     * @param {CookieConsent.CookieConsentConfig} consentConfig        - {@link https://cookieconsent.orestbida.com/reference/configuration-reference.html}.
     * @param {LocomotiveGoogleConsentTypeMap}    googleConsentTypeMap - Map of cookie consent categories and Google consent mode types.
     */
    run(consentConfig, googleConsentTypeMap) {
        if (!consentConfig?.categories || Object.keys(consentConfig.categories).length === 0) {
            console.warn('[Locomotive Cookie Consent]', 'Cookie consent is not correctly configured.');
            return;
        }

        if (!document?.body) {
            console.warn('[Locomotive Cookie Consent]', 'Document body is unavailable. Run manager after "DOMContentLoaded" event.');
            return;
        }

        this.consentConfig = consentConfig;
        this.googleConsentTypeMap = googleConsentTypeMap ?? {
            analytics: {
                analytics_storage: null,
            },
            targeting: {
                ad_personalization: null,
                ad_storage: null,
                ad_user_data: null,
            },
        };

        this.consentConfig.categories = this.prepareCategories(this.consentConfig.categories);
        this.consentConfig.autoShow = this.shouldShowOnLoad();

        if (window.gtag) {
            this.onConsent = this.onConsent.bind(this);
            this.overrideCallback(this.consentConfig, 'onConsent', this.onConsent);
            this.onChange = this.onChange.bind(this);
            this.overrideCallback(this.consentConfig, 'onChange', this.onChange);
        }

        this.onModalReady = this.onModalReady.bind(this);
        this.overrideCallback(this.consentConfig, 'onModalReady', this.onModalReady);

        CookieConsent.run(this.consentConfig);

        this.addEventListenersToCookieConsentDocument = this.addEventListenersToCookieConsentDocument.bind(this);
        this.addEventListeners();
    }

    shouldShowOnLoad() {
        const searchParams = new URLSearchParams(window.location.search);
        const hideConsent = searchParams.get('hide-cookie-consent');

        return !(hideConsent === 'true' || hideConsent === '');
    }

    prepareCategories(categories) {
        for (const [categoryName, categoryData] of Object.entries(categories)) {
            if (
                categoryData?.autoClear?.cookies &&
                Array.isArray(categoryData.autoClear.cookies) &&
                categoryData.autoClear.cookies.length > 0
            ) {
                categoryData.autoClear.cookies.map((cookie) => {
                    if (
                        cookie?.name &&
                        typeof cookie.name === 'string'
                    ) {
                        const found = cookie.name.match(/^\/(.+)\/([a-z]+)?$/);
                        if (found) {
                            cookie.name = new RegExp(found[1], found[2]);
                        }
                    }

                    return cookie;
                });

                categories[categoryName].autoClear.cookies = categoryData.autoClear.cookies;
            }
        }

        return categories;
    }

    /**
     * @param  {Location|HTMLAnchorElement} location
     * @return boolean
     */
    isLocationShowCookieConsent(location) {
        return (
            location?.search?.includes('show-cookie-consent') ||
            location?.hash === '#show-cookie-consent'
        );
    }

    /**
     * @param  {Location|HTMLAnchorElement} location
     * @return boolean
     */
    isLocationHideCookieConsent(location) {
        return (
            location?.search?.includes('hide-cookie-consent') ||
            location?.hash === '#hide-cookie-consent'
        );
    }

    /**
     * Overrides an existing function on a target object.
     *
     * @param {Object}   target   - The target object.
     * @param {string}   callback - The target object key to override.
     * @param {Function} override - The function to wrap.
     */
    overrideCallback(target, callback, override) {
        const callbackType = typeof target[callback];

        if (callbackType === 'undefined') {
            target[callback] = override;
            return;
        }

        if (callbackType === 'function') {
            target[`_${callback}`] = target[callback];
            target[callback] = (...args) => {
                target[`_${callback}`](...args);
                override(...args);
            };
            return;
        }
    }

    onConsent() {
        this.updateGtagConsent(Object.keys(this.googleConsentTypeMap));
    }

    onChange({ changedCategories }) {
        this.updateGtagConsent(changedCategories);
    }

    onModalReady() {
        this.addEventListenersToCookieConsentContainer();
    }

    addEventListeners() {
        this.addEventListenersToCookieConsentDocument();
    }

    addEventListenersToCookieConsentContainer() {
        const container = document.querySelector('#cc-main');
        if (!container) {
            return;
        }

        // Store event listener on element to ensure it is registered once.
        container.locomotiveHideCookieConsentByLocation = (event) => {
            if (event.target.tagName === 'A' && this.isLocationHideCookieConsent(event.target)) {
                this.hideModals();
            }
        };
        container.addEventListener('click', container.locomotiveHideCookieConsentByLocation, { passive: true });
    }

    addEventListenersToCookieConsentDocument() {
        // Store event listener on element to ensure it is registered once.
        document.locomotiveShowCookieConsentByLocation = (event) => {
            if (event.target.tagName === 'A' && this.isLocationShowCookieConsent(event.target)) {
                event.preventDefault();
                this.showPreferencesModal();
            }
        };
        document.addEventListener('click', document.locomotiveShowCookieConsentByLocation, { capture: true });
    }

    removeEventListeners() {
        this.removeEventListenersToCookieConsentDocument();
    }

    removeEventListenersToCookieConsentContainer() {
        const container = document.querySelector('#cc-main');
        if (!container) {
            return;
        }

        if (container.locomotiveHideCookieConsentByLocation) {
            container.removeEventListener('click', container.locomotiveHideCookieConsentByLocation);
        }
    }

    removeEventListenersToCookieConsentDocument() {
        if (document.locomotiveShowCookieConsentByLocation) {
            document.removeEventListener('click', document.locomotiveShowCookieConsentByLocation, { capture: true });
        }
    }

    /**
     * @param {string[]} categories - The cookie consent categories.
     */
    updateGtagConsent(categories) {
        const aggregateConsentState = {};
        for (const category of categories) {
            if (!this.googleConsentTypeMap[category]) {
                continue;
            }

            const consentState = this.parseGtagConsentCategory(category, this.googleConsentTypeMap[category]);
            for (const consentType in consentState) {
                if (!Object.hasOwn(consentState, consentType)) {
                    continue;
                }

                const consentFlag = consentState[consentType];
                if (
                    (typeof aggregateConsentState[consentType] === 'undefined') ||
                    consentFlag === 'denied'
                ) {
                    aggregateConsentState[consentType] = consentFlag;
                }
            }
        }

        gtag('consent', 'update', aggregateConsentState);
        gtag('event', 'consent_updated');
    }

    /**
     * @param  {string} category - The cookie consent category.
     */
    updateGtagConsentForCategory(category) {
        if (!this.googleConsentTypeMap[category]) {
            console.warn('[Locomotive Cookie Consent]', 'Category is not mapped to a Google consent mode.');
            return;
        }

        const consentState = this.parseGtagConsentCategory(category, this.googleConsentTypeMap[category]);
        gtag('consent', 'update', consentState);
        gtag('event', 'consent_updated');
    }

    /**
     * @param  {string}               category     - The cookie consent category.
     * @param  {GoogleConsentTypeMap} consentTypes - The Google Analytics consent types.
     * @return {GoogleConsentTypeMap} The resolved consent state for the given category.
     */
    parseGtagConsentCategory(category, consentTypes) {
        const consentFlag = CookieConsent?.acceptedCategory(category) ? 'granted' : 'denied';

        return Object.fromEntries(
            Object.keys(consentTypes)
                .map((consentType) => [consentType, consentFlag])
        );
    }

    hideModals() {
        CookieConsent.hidePreferences();
        CookieConsent.hide();
    }

    showPreferencesModal() {
        CookieConsent.showPreferences();
    }
}
